/**
 * AudienceFieldSelector
 *
 * Автономный компонент для выбора полей audience
 * - Загружает данные из Firestore самостоятельно
 * - Использует rc-tree для отображения дерева
 * - Поддерживает формат путей: @audience[ID].actors[ID].field[index]
 * - Оптимизирует пути: если выбраны все дети, сохраняет только путь родителя
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Box, CircularProgress, Typography, LinearProgress, FormControlLabel, Switch } from '@mui/material';
import Tree from 'rc-tree';
import 'rc-tree/assets/index.css';
import type { Key } from 'rc-tree/lib/interface';

import { AudienceService } from '@modules/firebase-core/services/AudienceService';
import type { Audience } from '@segmently/schema-models/entities';

import type { AudienceFieldSelectorProps, AudienceTreeNode } from './types';
import { audienceToTreeNode } from './audienceToTreeNode';
import { treeNodeIdsToPaths, pathsToTreeNodeIds, deduplicatePaths } from './pathConverter';
import { pathsToJson } from './pathsToJson';

/**
 * AudienceFieldSelector Component
 */
export const AudienceFieldSelector: React.FC<AudienceFieldSelectorProps> = ({
  projectId,
  value,
  onChange,
  height = 400,
  selectAll = false,
  onHydratedDataChange,
  highlightEmptyAudiences = true,
}) => {
  const [audiences, setAudiences] = useState<Audience[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadProgress, setLoadProgress] = useState({ loaded: 0, total: 0, currentItem: '' });
  const [highlightEmpty, setHighlightEmpty] = useState(highlightEmptyAudiences);

  // Загружаем audiences с actors и subcollections из Firestore
  useEffect(() => {
    let isMounted = true;

    const loadAudiences = async () => {
      try {
        setLoading(true);
        setError(null);
        setLoadProgress({ loaded: 0, total: 0, currentItem: 'Initializing...' });

        const audienceService = new AudienceService(projectId);

        // Используем новый метод с прогрессом
        const loadedAudiences = await audienceService.listWithActorsAndSubcollections(
          (loaded, total, currentItem) => {
            if (isMounted) {
              setLoadProgress({ loaded, total, currentItem });
            }
          }
        );

        if (isMounted) {
          setAudiences(loadedAudiences);
        }
      } catch (err) {
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Failed to load audiences');
          console.error('Failed to load audiences:', err);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadAudiences();

    return () => {
      isMounted = false;
    };
  }, [projectId]);

  // Handle selectAll: auto-select audiences after loading
  // When highlightEmpty is ON, only select audiences that have actors
  useEffect(() => {
    if (selectAll && audiences.length > 0 && value.length === 0 && !loading) {
      const audiencesToSelect = highlightEmpty
        ? audiences.filter((a) => a.actors && a.actors.length > 0)
        : audiences;
      const allAudiencePaths = audiencesToSelect.map((audience) => `@audience[${audience.id}]`);
      onChange(allAudiencePaths);
    }
  }, [selectAll, audiences, value.length, loading, onChange, highlightEmpty]);

  // Generate hydrated data when value or audiences change
  useEffect(() => {
    if (onHydratedDataChange && audiences.length > 0) {
      if (value.length > 0) {
        const hydratedData = pathsToJson(value, audiences);
        onHydratedDataChange(hydratedData);
      } else {
        // Clear hydrated data when selection is empty
        onHydratedDataChange({ audiences: [] });
      }
    }
  }, [value, audiences, onHydratedDataChange]);

  // Конвертируем audiences в дерево (с сортировкой: пустые в конец)
  const treeData = useMemo<AudienceTreeNode[]>(() => {
    const sorted = highlightEmpty
      ? [...audiences].sort((a, b) => {
          const aHas = a.actors?.length ? 1 : 0;
          const bHas = b.actors?.length ? 1 : 0;
          return bHas - aHas;
        })
      : audiences;
    return sorted.map((audience) => audienceToTreeNode(audience, highlightEmpty));
  }, [audiences, highlightEmpty]);

  // Конвертируем value (пути) в checkedKeys (ключи узлов)
  const checkedKeys = useMemo<Key[]>(() => {
    if (!value || value.length === 0) return [];
    return pathsToTreeNodeIds(value, treeData);
  }, [value, treeData]);

  // Обработчик изменения выбора
  const handleCheck = (checkedKeysValue: Key[] | { checked: Key[]; halfChecked: Key[] }) => {
    // rc-tree может возвращать либо массив, либо объект с checked/halfChecked
    const checkedArray = Array.isArray(checkedKeysValue)
      ? checkedKeysValue
      : checkedKeysValue.checked;

    const stringKeys = checkedArray.map((key) => String(key));

    // Конвертируем ключи узлов обратно в пути
    const paths = treeNodeIdsToPaths(stringKeys, treeData);

    // Умная дедупликация: удаляет child пути ТОЛЬКО если ВСЕ дети выбраны
    // Это оптимизирует исходящие данные (например, ["@audience[ID].field1", ...] → ["@audience[ID]"])
    const dedupedPaths = deduplicatePaths(paths, treeData);

    onChange(dedupedPaths);
  };

  // Состояния загрузки и ошибки
  if (loading) {
    const progressPercent = loadProgress.total > 0
      ? Math.round((loadProgress.loaded / loadProgress.total) * 100)
      : 0;

    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height,
          flexDirection: 'column',
          gap: 2,
          px: 4,
        }}
      >
        <CircularProgress size={40} />

        <Box sx={{ width: '100%', maxWidth: 400 }}>
          <LinearProgress
            variant={loadProgress.total > 0 ? 'determinate' : 'indeterminate'}
            value={progressPercent}
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>

        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {loadProgress.currentItem || 'Loading audiences...'}
          </Typography>
          {loadProgress.total > 0 && (
            <Typography variant="caption" color="text.secondary">
              {loadProgress.loaded} / {loadProgress.total} ({progressPercent}%)
            </Typography>
          )}
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height,
          flexDirection: 'column',
          gap: 1,
        }}
      >
        <Typography variant="body1" color="error">
          Error loading data
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {error}
        </Typography>
      </Box>
    );
  }

  if (audiences.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          No audiences found
        </Typography>
      </Box>
    );
  }

  // Отображаем дерево
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={highlightEmpty}
              onChange={(_, checked) => setHighlightEmpty(checked)}
            />
          }
          label="Highlight empty"
          slotProps={{ typography: { variant: 'caption', color: 'text.secondary' } }}
        />
      </Box>
      <Box
        sx={{
          height,
          overflow: 'auto',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          p: 2,
        }}
      >
        <Tree
          treeData={treeData}
          checkable
          checkedKeys={checkedKeys}
          onCheck={handleCheck}
          defaultExpandAll={false}
          showIcon={false}
        />
      </Box>
    </Box>
  );
};
